import { useEffect, useState } from 'react';

/**
 * Android-only keyboard detection for WebView.
 * iOS handles the keyboard natively — no JS intervention needed there.
 *
 * Detection layers (first one that fires wins):
 *  1. visualViewport resize/scroll (Chrome/Edge on Android)
 *  2. window.resize (adjustResize WebViews)
 *  3. focusin polling at 100/250/400ms
 *  4. fallback estimate (38% of viewport) if nothing reported after 500ms
 *
 * Returns the keyboard offset in px (0 when closed).
 */
export const useAndroidKeyboard = (): number => {
  const [keyboardOffset, setKeyboardOffset] = useState(0);

  useEffect(() => {
    if (!/android/i.test(navigator.userAgent)) return;

    const vv = window.visualViewport;
    let pendingTimers: ReturnType<typeof setTimeout>[] = [];
    let inputFocused = false;
    let keyboardDetected = false;

    const calculateOffset = (): boolean => {
      if (vv) {
        const offset = window.innerHeight - vv.height;
        if (offset > 50) {
          keyboardDetected = true;
          setKeyboardOffset(offset);
          return true;
        }
      }
      return false;
    };

    if (vv) {
      vv.addEventListener('resize', calculateOffset);
      vv.addEventListener('scroll', calculateOffset);
    }
    window.addEventListener('resize', calculateOffset);

    const onFocusIn = (e: FocusEvent) => {
      if (e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLInputElement) {
        inputFocused = true;
        keyboardDetected = false;

        [100, 250, 400].forEach((delay) => {
          const t = setTimeout(() => calculateOffset(), delay);
          pendingTimers.push(t);
        });

        const fallbackTimer = setTimeout(() => {
          if (inputFocused && !keyboardDetected) {
            setKeyboardOffset(Math.round(window.innerHeight * 0.38));
          }
        }, 500);
        pendingTimers.push(fallbackTimer);
      }
    };

    const onFocusOut = (e: FocusEvent) => {
      const related = e.relatedTarget as HTMLElement | null;
      if (!related || (!(related instanceof HTMLTextAreaElement) && !(related instanceof HTMLInputElement))) {
        inputFocused = false;
        keyboardDetected = false;
        const t = setTimeout(() => {
          if (!inputFocused) setKeyboardOffset(0);
        }, 100);
        pendingTimers.push(t);
      }
    };

    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);

    return () => {
      if (vv) {
        vv.removeEventListener('resize', calculateOffset);
        vv.removeEventListener('scroll', calculateOffset);
      }
      window.removeEventListener('resize', calculateOffset);
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
      pendingTimers.forEach(clearTimeout);
    };
  }, []);

  return keyboardOffset;
};
